import { useQuery } from '@apollo/client';
import { useOktaAuth } from '@okta/okta-react';
import { GET_MY_ROLES } from '../graphql/asset.queries';
import { AppRole } from '../interfaces/IAsset';

interface UseAppRolesResult {
  roles: AppRole[];
  loading: boolean;
  hasAny: (...required: AppRole[]) => boolean;
  isSuperAdmin: boolean;
  isAssetAdmin: boolean;
  isAssetViewer: boolean;
  isDeskbookingAdmin: boolean;
  isLunchAdmin: boolean;
  isReportsViewer: boolean;
}

/**
 * Returns the granular app roles for the currently authenticated user.
 * SUPER_ADMIN is treated as a wildcard that satisfies any other role check.
 */
export function useAppRoles(): UseAppRolesResult {
  const { authState } = useOktaAuth();
  const skip = !authState?.isAuthenticated;

  const { data, loading } = useQuery<{ myRoles: AppRole[] }>(GET_MY_ROLES, {
    skip,
    fetchPolicy: 'cache-first',
  });

  const roles = data?.myRoles ?? [];
  const isSuperAdmin = roles.includes(AppRole.SUPER_ADMIN);

  const hasAny = (...required: AppRole[]): boolean => {
    if (required.length === 0) return true;
    if (isSuperAdmin) return true;
    return required.some((r) => roles.includes(r));
  };

  return {
    roles,
    loading,
    hasAny,
    isSuperAdmin,
    isAssetAdmin: hasAny(AppRole.ASSET_ADMIN),
    isAssetViewer: hasAny(AppRole.ASSET_VIEWER),
    isDeskbookingAdmin: hasAny(AppRole.DESKBOOKING_ADMIN),
    isLunchAdmin: hasAny(AppRole.LUNCH_ADMIN),
    isReportsViewer: hasAny(AppRole.REPORTS_VIEWER),
  };
}
